import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { auth, db } from "../firebase/config";
import {
  setAuthCookie,
  setAuthSessionFlag,
  removeAuthCookie,
} from "../firebase/services/authToken";
import { createUserPrivateData } from "../firebase/services/userPrivateServices";
import { getUserPrivacySettings } from "../firebase/services/userServices";

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

const CHECKLIST_ITEMS = {
  PROFILE: "profile",
  FIRST_BOOK: "firstBook",
  CREATE_LIST: "createList",
  WRITE_REVIEW: "writeReview",
  FOLLOW_READERS: "followReaders",
};

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboardingChecklist, setShowOnboardingChecklist] = useState(false);
  const [checklistStatus, setChecklistStatus] = useState({});
  const [allTasksCompleted, setAllTasksCompleted] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, "Users", user.uid));
        const userData = userDoc.data();

        // Check if user is suspended
        if (userData?.status === "suspended") {
          await signOut(auth);
          setUser(null);
          removeAuthCookie();
          setLoading(false);
          return;
        }

        // Determine if we should show the onboarding checklist
        const shouldShowChecklist =
          !userData?.onboardingStatus?.checklistDismissed &&
          Array.isArray(userData?.onboardingStatus?.checklistProgress) &&
          userData.onboardingStatus.checklistProgress.length === 0;
        setShowOnboardingChecklist(shouldShowChecklist);

        // Fetch privacy settings
        const privacySettings = await getUserPrivacySettings(user.uid);

        setUser({
          uid: user.uid,
          email: user.email,
          username: userData?.username,
          bio: userData?.bio,
          joinDate: userData?.joinDate,
          following: userData?.following || 0,
          followers: userData?.followers || 0,
          booksRead: userData?.booksRead || 0,
          currentStreak: userData?.currentStreak || 0,
          profileImage: userData?.profileImage || "/avatars/image.png",
          role: userData?.role || "user",
          isAdmin: userData?.role === "admin",
          status: userData?.status || "active",
          privacySettings: privacySettings,
          onboardingStatus: userData?.onboardingStatus || {
            lastStep: "initial",
            profileCompleted: false,
            preferencesCompleted: false,
            checklistProgress: [],
            checklistDismissed: false,
          },
        });

        setAuthSessionFlag();
        await setAuthCookie();
      } else {
        setUser(null);
        removeAuthCookie();
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Add this new useEffect for real-time checklist monitoring
  useEffect(() => {
    if (!user?.uid) return;

    // Create listeners for each collection we need to monitor
    const listeners = [
      // Profile changes
      onSnapshot(doc(db, "Users", user.uid), (doc) => {
        const userData = doc.data();
        const profileComplete = Boolean(
          userData?.bio &&
            userData?.profileImage &&
            userData?.profileImage !== "/avatars/image.png"
        );
        updateCompletionStatus("profile", profileComplete);
      }),

      // Books
      onSnapshot(
        query(
          collection(db, "UserBooks"),
          where("userId", "==", user.uid),
          limit(1)
        ),
        (snapshot) => updateCompletionStatus("firstBook", !snapshot.empty)
      ),

      // Lists
      onSnapshot(
        query(
          collection(db, "ReadingLists"),
          where("userId", "==", user.uid),
          limit(1)
        ),
        (snapshot) => updateCompletionStatus("createList", !snapshot.empty)
      ),

      // Reviews
      onSnapshot(
        query(
          collection(db, "Reviews"),
          where("userId", "==", user.uid),
          limit(1)
        ),
        (snapshot) => updateCompletionStatus("writeReview", !snapshot.empty)
      ),

      // Follows
      onSnapshot(
        query(
          collection(db, "UserFollows"),
          where("followerId", "==", user.uid),
          limit(1)
        ),
        (snapshot) => updateCompletionStatus("followReaders", !snapshot.empty)
      ),
    ];

    // Cleanup listeners
    return () => listeners.forEach((unsubscribe) => unsubscribe());
  }, [user?.uid]);

  const updateCompletionStatus = (itemId, isComplete) => {
    setChecklistStatus((prev) => {
      const newStatus = { ...prev, [itemId]: isComplete };

      // Check if all tasks are now completed
      const allCompleted =
        Object.values(newStatus).filter(Boolean).length ===
        CHECKLIST_ITEMS.length;
      if (allCompleted && !allTasksCompleted) {
        setAllTasksCompleted(true);
      }

      return newStatus;
    });
  };

  const validateUsername = async (username) => {
    // Check username format
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    if (!usernameRegex.test(username)) {
      throw new Error(
        "Username must be 3-20 characters long and can only contain letters, numbers, and underscores"
      );
    }

    // Check if username already exists
    const usersRef = collection(db, "Users");
    const q = query(
      usersRef,
      where("usernameLower", "==", username.toLowerCase())
    );
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      throw new Error("Username is already taken");
    }
  };

  const signup = async (email, password, username, additionalData = {}) => {
    // Validate username before creating account
    await validateUsername(username);

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        throw new Error("Please enter a valid email address");
      }

      // Validate password strength
      if (password.length < 6) {
        throw new Error("Password must be at least 6 characters long");
      }

      // Create user account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Prepare user data with new fields (NO EMAIL - it goes in UserPrivate)
      const userData = {
        username,
        bio: additionalData.bio || "",
        displayName: additionalData.displayName || username,
        joinDate: new Date().getFullYear().toString(),
        following: 0,
        followers: 0,
        booksRead: 0,
        currentStreak: 0,
        profileImage: additionalData.profileImage || "/avatars/image.png",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        usernameLower: username.toLowerCase(),
        displayNameLower: (
          additionalData.displayName || username
        ).toLowerCase(),
        role: "user",
        status: "active",
        onboardingStatus: {
          profileCompleted:
            !!additionalData.displayName || !!additionalData.bio,
          preferencesCompleted: !!additionalData.readingPreferences,
          checklistProgress: [],
          lastStep: additionalData.readingPreferences
            ? "preferences"
            : additionalData.displayName
            ? "profile"
            : "initial",
        },
        settings: {
          emailNotifications: true,
          darkMode: false,
          privacyLevel: "public",
          ...additionalData.settings,
        },
        stats: {
          totalPagesRead: 0,
          averageRating: 0,
          reviewsWritten: 0,
          listsCreated: 0,
        },
        readingPreferences: additionalData.readingPreferences || {
          favoriteGenres: [],
          readingGoal: 12,
          preferredFormats: [],
          readingLanguages: [],
        },
      };

      // Prepare private data (email and sensitive info)
      const privateData = {
        email,
        phoneNumber: additionalData.phoneNumber || null,
        notificationPreferences: {
          email: userData.settings.emailNotifications,
          push: true,
          newFollower: true,
          clubActivity: true,
        },
      };

      // Save to Firestore - create both documents
      await Promise.all([
        setDoc(doc(db, "Users", user.uid), userData),
        createUserPrivateData(user.uid, privateData),
      ]);

    setAuthSessionFlag();
    await setAuthCookie();
    return user;
  };

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Check if user is suspended before completing login
      const userDoc = await getDoc(doc(db, "Users", userCredential.user.uid));
      const userData = userDoc.data();

      if (userData?.status === "suspended") {
        // Sign out immediately if suspended
        await signOut(auth);
        throw new Error(
          "Your account has been suspended. Please contact support for assistance."
        );
      }

      setAuthSessionFlag();
      await setAuthCookie();
      return userCredential;
    } catch (error) {
      if (error.message.includes("suspended")) {
        throw error;
      }
      throw new Error("Failed to login. Please check your credentials.");
    }
  };

  const logout = async () => {
    setUser(null);
    // Remove the auth cookie before signing out
    removeAuthCookie();
    await signOut(auth);
  };

  // Add new method to update onboarding progress
  const updateOnboardingProgress = async (userId, step, data) => {
    try {
      const userRef = doc(db, "Users", userId);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();

      let updates = {
        updatedAt: new Date().toISOString(),
        "onboardingStatus.lastStep": step,
      };

      switch (step) {
        case "profile":
          updates = {
            ...updates,
            displayName: data.displayName || userData.displayName,
            bio: data.bio || userData.bio,
            profileImage: data.profileImage || userData.profileImage,
            displayNameLower: (
              data.displayName || userData.displayName
            ).toLowerCase(),
            "onboardingStatus.profileCompleted": true,
          };
          break;

        case "preferences":
          updates = {
            ...updates,
            readingPreferences: {
              ...userData.readingPreferences,
              ...data,
            },
            "onboardingStatus.preferencesCompleted": true,
          };
          break;

        case "checklist":
          updates = {
            ...updates,
            "onboardingStatus.checklistProgress": data,
          };
          break;
      }

      await setDoc(userRef, updates, { merge: true });

      // Update local user state
      setUser((prev) => ({
        ...prev,
        ...updates,
      }));
    } catch (error) {
      console.error("Error updating onboarding progress:", error);
      throw error;
    }
  };

  // Modify the isChecklistItemCompleted to use the real-time status
  const isChecklistItemCompleted = (itemId) => {
    return checklistStatus[itemId] || false;
  };

  // Add new method to complete a checklist item
  const completeChecklistItem = async (itemId) => {
    if (!user || isChecklistItemCompleted(itemId)) return;

    try {
      const newProgress = [
        ...(user.onboardingStatus?.checklistProgress || []),
        itemId,
      ];

      await updateOnboardingProgress(user.uid, "checklist", newProgress);

      // If all items are completed, automatically dismiss the checklist
      if (newProgress.length === Object.keys(CHECKLIST_ITEMS).length) {
        await dismissChecklist();
      }

      return true;
    } catch (error) {
      console.error("Error completing checklist item:", error);
      return false;
    }
  };

  // Add new method to dismiss the checklist
  const dismissChecklist = async () => {
    if (!user) return;

    try {
      const userRef = doc(db, "Users", user.uid);

      // Update the document directly with the correct structure
      await setDoc(
        userRef,
        {
          updatedAt: new Date().toISOString(),
          onboardingStatus: {
            ...user.onboardingStatus,
            checklistProgress: [],
            checklistDismissed: true,
          },
        },
        { merge: true }
      );

      // Update local state
      setUser((prev) => ({
        ...prev,
        onboardingStatus: {
          ...prev.onboardingStatus,
          checklistProgress: [],
          checklistDismissed: true,
        },
      }));

      // Update local checklist visibility
      setShowOnboardingChecklist(false);

      return true;
    } catch (error) {
      console.error("Error dismissing checklist:", error);
      return false;
    }
  };

  // Add method to refresh privacy settings
  const refreshPrivacySettings = async () => {
    if (!user?.uid) return;

    try {
      const privacySettings = await getUserPrivacySettings(user.uid);
      setUser((prev) => ({
        ...prev,
        privacySettings: privacySettings,
      }));
    } catch (error) {
      console.error("Error refreshing privacy settings:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        signup,
        logout,
        updateOnboardingProgress,
        showOnboardingChecklist,
        isChecklistItemCompleted,
        completeChecklistItem,
        dismissChecklist,
        allTasksCompleted,
        setAllTasksCompleted,
        refreshPrivacySettings,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};
